import { useEffect, useState } from 'react';
import Sidebar from '../components/layout/Sidebar';
import { apiFetchWithRetry } from '../services/apiFetch';

interface EvidenceItem {
  id: string; requestId: string; name: string; title: string; organization: string;
  description: string; sourceUrl: string; proposedClaim: string; note: string;
}
function ReviewCard({ item, onDone }: { item: EvidenceItem; onDone: () => void }) {
  const [claim, setClaim] = useState(item.proposedClaim);
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function decide(confirm: boolean) {
    setBusy(true); setError('');
    try {
      const response = await apiFetchWithRetry('/v1/admin/source-evidence/' + encodeURIComponent(item.id) + '/decide', {
        method: 'POST', body: JSON.stringify({ requestId: item.requestId, confirm, claim, note }),
        signal: AbortSignal.timeout(20_000),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || 'Decision failed');
      onDone();
    } catch (e) { setError(e instanceof Error ? e.message : 'Please retry.'); }
    finally { setBusy(false); }
  }
  return <article className="rounded-xl border bg-white p-5 space-y-3">
    <h2 className="text-lg font-semibold">{item.title}</h2>
    <p>{item.name} · {item.organization}</p><p className="text-sm text-gray-600">{item.description}</p>
    <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer" className="block break-all text-blue-700 underline">Open evidence: {item.sourceUrl}</a>
    <p className="text-sm text-amber-800">Automatic check: {item.note}</p>
    <label className="block text-sm font-medium">Exact statement supported by this source
      <textarea value={claim} onChange={(e) => setClaim(e.target.value)} maxLength={500} className="mt-1 block w-full rounded border p-2" />
    </label>
    <label className="block text-sm font-medium">Review note (shown to the owner)
      <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} className="mt-1 block w-full rounded border p-2" />
    </label>
    <label className="flex gap-2 text-sm"><input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />I checked the public source, the person's connection, and the exact statement above.</label>
    <div className="flex gap-3">
      <button type="button" disabled={busy || !confirmed || !claim.trim() || !note.trim()} onClick={() => decide(true)} className="rounded bg-teal-700 px-4 py-2 text-white disabled:opacity-40">Verify statement</button>
      <button type="button" disabled={busy || !note.trim()} onClick={() => decide(false)} className="rounded border px-4 py-2 disabled:opacity-40">Cannot confirm</button>
    </div>
    {error && <p role="alert" className="text-red-700">{error}</p>}
  </article>;
}
export default function SourceEvidenceReview() {
  const [items, setItems] = useState<EvidenceItem[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  async function load(cursor?: string) {
    setLoading(true); setError('');
    try {
      const response = await apiFetchWithRetry('/v1/admin/source-evidence/queue' + (cursor ? '?cursor=' + encodeURIComponent(cursor) : ''), { signal: AbortSignal.timeout(20_000) });
      if (!response.ok) throw new Error('Could not load source reviews.');
      const data = await response.json(); setItems((old) => cursor ? [...old, ...data.items] : data.items); setNextCursor(data.nextCursor);
    } catch (e) { setError(e instanceof Error ? e.message : 'Please retry.'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, []);
  return <div className="flex min-h-screen bg-gray-50"><Sidebar /><main className="ml-64 w-full max-w-5xl p-8">
    <div className="mb-6 flex items-center justify-between"><h1 className="text-2xl font-bold">Public source reviews</h1><button type="button" onClick={() => load()} disabled={loading} className="rounded border px-4 py-2">Refresh</button></div>
    <p className="mb-5 text-gray-600">Confirm only what the evidence proves. Company reviews alone do not establish an individual's role.</p>
    {error && <p role="alert" className="mb-4 text-red-700">{error}</p>}
    {loading ? <p>Loading…</p> : <div className="space-y-5">{items.map((item) => <ReviewCard key={item.requestId} item={item} onDone={() => setItems((old) => old.filter((x) => x.requestId !== item.requestId))} />)}{!items.length && !error && <p>No sources awaiting review.</p>}</div>}
    {nextCursor && <button type="button" disabled={loading} onClick={() => load(nextCursor)} className="mt-4 rounded border px-4 py-2">Load more reviews</button>}
  </main></div>;
}
